/* eslint-disable jsdoc/require-param */
/* eslint-disable jsdoc/check-param-names */
import React, { useState, useCallback, useMemo } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  MenuItem,
  Switch,
  FormControlLabel,
  Grid,
  IconButton,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Close as CloseIcon } from "@material-ui/icons";
import DeleteForeverIcon from "@material-ui/icons/DeleteForever";
import nanoId from "nano-id";

const useStyles = makeStyles(theme => ({
  modal: {
    width: 480,
    padding: theme.spacing(3),
    borderRadius: 12,
  },
  header: {
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(3),
  },
  title: {
    flexGrow: 1,
    display: "flex",
    flexDirection: "column",
  },
  inputContainer: {
    marginBottom: theme.spacing(2),
  },
  requiredLabel: {
    color: "#666",
    fontSize: "0.75rem",
  },
  addButton: {
    backgroundColor: theme.palette.primary.main,
    color: "white",
    "&:hover": {
      backgroundColor: theme.palette.primary.dark,
    },
  },
  cancelButton: {
    color: "#e76f51",
    marginRight: theme.spacing(2),
  },
  previewContainer: {
    backgroundColor: "#f5f5f5",
    padding: theme.spacing(2),
    borderRadius: 8,
    marginTop: theme.spacing(2),
  },
  dynamicTag: {
    backgroundColor: theme.palette.primary.light,
    color: theme.palette.primary.main,
    padding: "2px 8px",
    borderRadius: 16,
    fontSize: "0.75rem",
    marginLeft: theme.spacing(1),
  },
}));

/**
 * A modal component for adding or editing a supplier link.
 *
 * @param {object} [restVar={}] - The object containing all available ZIMS variables.
 * @param {Function} [addNewLink=() => {}] - The callback function to call when the user submits the form.
 * @param {Function} onClose - The callback function to call when the user closes the modal.
 * @param {object} [supLink={}] - The supplier link object.
 * @param {boolean} [edit=false] - Whether the modal is in edit mode or not.
 */
export function AddSupplierLinkModal({
  restVar = {},
  addNewLink = () => {},
  onClose,
  supLink = {
    link_name: "",
    link_address: "",
    rest_var: [],
  },
  edit = false,
}) {
  const classes = useStyles();

  // Initial state setup
  const initialLink = useMemo(
    () => ({
      ...supLink,
      rest_var: (supLink.rest_var || []).map(item => {
        try {
          return typeof item === "string" ? JSON.parse(item) : item;
        } catch (e) {
          console.error("Failed to parse item in rest_var:", item, e);
          return null; // or return item if you want to keep the original on failure
        }
      }),
    }),
    [supLink]
  );

  const [formInput, setFormInput] = useState(initialLink);
  const [zimsVar, setZimsVar] = useState({
    q: "",
    var: "",
    is_static: false,
  });

  // Handlers for form inputs
  const handleFormInput = useCallback(e => {
    const { name, value } = e.target;
    setFormInput(prev => ({
      ...prev,
      [name]: value,
    }));
  }, []);

  const handleZimsVarSelect = useCallback(event => {
    setZimsVar(prev => ({
      ...prev,
      var: event.target.value,
    }));
  }, []);

  const handleZimsVar = useCallback(event => {
    setZimsVar(prev => ({
      ...prev,
      q: event.target.value,
    }));
  }, []);

  const handleZimsStatic = useCallback(event => {
    setZimsVar(prev => ({
      ...prev,
      var: event.target.value,
    }));
  }, []);

  const toggleZimsVarStatic = useCallback(() => {
    setZimsVar(prev => ({
      ...prev,
      var: "",
      is_static: !prev.is_static,
    }));
  }, []);

  // Add REST variable to form
  const submitZimsVar = useCallback(() => {
    if (!zimsVar.q || !zimsVar.var) return;

    setFormInput(prev => ({
      ...prev,
      rest_var: [
        ...prev.rest_var,
        {
          ...zimsVar,
          id: nanoId(),
        },
      ],
    }));
    setZimsVar({ q: "", var: "", is_static: false });
  }, [zimsVar]);

  // Remove REST variable from form
  const removeZimsVar = useCallback(zimsVarId => {
    setFormInput(prev => ({
      ...prev,
      rest_var: prev.rest_var.filter(item => item.id !== zimsVarId),
    }));
  }, []);

  // Convert REST variables to array for dropdown
  const restVarOptions = useMemo(() => {
    return Object.keys(restVar)
      .filter(key => key)
      .map(key => ({ value: key, label: key }));
  }, [restVar]);

  // Generate preview URL
  const generatePreviewUrl = useCallback(() => {
    const baseUrl = formInput.link_address;
    const queryParams = formInput.rest_var
      .map(
        param => `${param.q}=${param.is_static ? param.var : `{${param.var}}`}`
      )
      .join("&");

    return queryParams ? `${baseUrl}?${queryParams}` : baseUrl;
  }, [formInput]);

  // Submit handler
  const addNewLinkHandler = useCallback(() => {
    addNewLink(formInput);
  }, [addNewLink, formInput]);

  return (
    <Box className={classes.modal}>
      <Box className={classes.header}>
        <Box className={classes.title}>
          <Typography variant="h6">Add Supplier Link</Typography>
          <Typography variant="body2" color="textSecondary">
            Add a new link to your supplier links collection
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      <Box className={classes.inputContainer}>
        <TextField
          fullWidth
          label="Link Name"
          name="link_name"
          variant="outlined"
          value={formInput.link_name}
          onChange={handleFormInput}
          placeholder="e.g. Customer Support Central"
          InputProps={{
            endAdornment: (
              <Typography className={classes.requiredLabel}>
                Required
              </Typography>
            ),
          }}
        />
      </Box>
      <Box className={classes.inputContainer}>
        <TextField
          fullWidth
          label="Link Address"
          name="link_address"
          variant="outlined"
          value={formInput.link_address}
          onChange={handleFormInput}
          placeholder="e.g. https://app.zawadi.africa"
          InputProps={{
            endAdornment: (
              <Typography className={classes.requiredLabel}>
                Required
              </Typography>
            ),
          }}
        />
      </Box>

      {/* ZIMS Variables */}
      <Box className={classes.inputContainer}>
        <Typography variant="subtitle2" gutterBottom>
          REST Query Parameters
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <TextField
              label="Query Parameter"
              value={zimsVar.q}
              variant="outlined"
              fullWidth
              helperText="e.g. z-code"
              onChange={handleZimsVar}
            />
          </Grid>
          <Grid item xs={6}>
            {zimsVar.is_static ? (
              <TextField
                label="Static Value"
                value={zimsVar.var}
                variant="outlined"
                fullWidth
                helperText="e.g. zawadi"
                onChange={handleZimsStatic}
              />
            ) : (
              <TextField
                select
                label="ZIMS Variable"
                value={zimsVar.var}
                variant="outlined"
                fullWidth
                onChange={handleZimsVarSelect}
                helperText="Select your ZIMS variable"
              >
                {restVarOptions.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            )}
          </Grid>
        </Grid>
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={zimsVar.is_static}
              onChange={toggleZimsVarStatic}
            />
          }
          label="Static Value"
        />
        <Button
          fullWidth
          color="primary"
          variant="contained"
          onClick={submitZimsVar}
          disabled={!zimsVar.q || !zimsVar.var}
          style={{ marginTop: 16 }}
        >
          Add REST Query Parameter
        </Button>
      </Box>

      {formInput.rest_var.map(item => (
        <Grid
          container
          key={item.id}
          alignItems="center"
          style={{ marginBottom: 8 }}
        >
          <Grid item xs={10}>
            <Typography>
              <code>{`${item.q}=${item.var}`}</code>
            </Typography>
          </Grid>
          <Grid item xs={2}>
            <IconButton onClick={() => removeZimsVar(item.id)} size="small">
              <DeleteForeverIcon />
            </IconButton>
          </Grid>
        </Grid>
      ))}

      {formInput.rest_var.length > 0 && (
        <Box className={classes.previewContainer}>
          <Typography variant="subtitle2" gutterBottom>
            Preview URL
          </Typography>
          <Typography variant="body2">{generatePreviewUrl()}</Typography>
        </Box>
      )}

      <Box display="flex" justifyContent="flex-end" mt={3}>
        <Button onClick={onClose} className={classes.cancelButton}>
          Cancel
        </Button>
        <Button
          variant="contained"
          className={classes.addButton}
          onClick={addNewLinkHandler}
        >
          {edit ? "Edit Link" : "Add Link"}
        </Button>
      </Box>
    </Box>
  );
}

export default AddSupplierLinkModal;
